import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  writeBatch,
  getDoc,
} from "firebase/firestore";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateEmail,
  updatePassword,
} from "firebase/auth";
import { db, auth } from "../firebase/firebase";
import { useAuth } from "../Context/AuthContext";
import { useWeddingData } from "../hooks/useWeddingData";
import "./Dashboard.scss";

import ButtonComponent from "../ButtonComponent/ButtonComponent";
import {
  FaUsers,
  FaClock,
  FaCheck,
  FaTimes,
  FaMale,
  FaFemale,
  FaEdit,
  FaSave,
} from "react-icons/fa";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";

const GROOM_COLLECTION = "GroomGuests";
const BRIDE_COLLECTION = "BrideGuests";

const Dashboard = () => {
  const { user } = useAuth();
  const { weddingData, loading: weddingLoading } = useWeddingData();

  const [groomGuests, setGroomGuests] = useState([]);
  const [brideGuests, setBrideGuests] = useState([]);
  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const [isUpdatingAccount, setIsUpdatingAccount] = useState(false);
  const [accountMessage, setAccountMessage] = useState("");

  const [formData, setFormData] = useState({
    bride_name: "",
    groom_name: "",
    wedding_date: "",
    ceremony_time: "",
    wedding_location: "",
    hotel_address: "",
    bride_parent_surname: "",
    groom_parent_surname: "",
    guest_count: "",
    ceremony_type: "wedding",
  });

  const [accountForm, setAccountForm] = useState({
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const bride_name = weddingData?.bride_name || "Bride";
  const groom_name = weddingData?.groom_name || "Groom";

  const groomCollectionName = useMemo(() => GROOM_COLLECTION, []);
  const brideCollectionName = useMemo(() => BRIDE_COLLECTION, []);

  useEffect(() => {
    const loadTheme = async () => {
      if (weddingLoading) return;

      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);

        if (!weddingData?.slug) {
          setSiteTheme(fallbackTheme);
          return;
        }

        const siteSnap = await getDoc(doc(db, "sites", weddingData.slug));

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading site theme:", error);
        setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
      } finally {
        setThemeLoading(false);
      }
    };

    loadTheme();
  }, [weddingData, weddingLoading]);

  const dashboardThemeStyle = useMemo(() => {
    return createThemeStyle(siteTheme || DEFAULT_THEME);
  }, [siteTheme]);

  const toDateObject = (value) => {
    if (!value) return null;

    try {
      if (value?.toDate) {
        return value.toDate();
      }

      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }

      return null;
    } catch {
      return null;
    }
  };

  const normalizeDateOnly = (dateValue) => {
    const date = toDateObject(dateValue);
    if (!date) return null;

    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  };

  const getNormalizedPlan = (data) => {
    if (data?.plan === "premium") return "premium";
    if (data?.plan === "trial") return "trial";
    if (data?.isTrial) return "trial";
    return "trial";
  };

  const currentPlan = getNormalizedPlan(weddingData);
  const isPremium = currentPlan === "premium";

  const expiryDate = useMemo(() => {
    if (!weddingData) return null;

    if (isPremium) {
      return normalizeDateOnly(weddingData.wedding_date);
    }

    return normalizeDateOnly(weddingData.expiry_date);
  }, [weddingData, isPremium]);

  const isExpired = useMemo(() => {
    if (!expiryDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today > expiryDate;
  }, [expiryDate]);

  const expiryText = expiryDate ? expiryDate.toLocaleDateString() : "—";

  useEffect(() => {
    if (!weddingData) return;

    setFormData({
      bride_name: weddingData?.bride_name || "",
      groom_name: weddingData?.groom_name || "",
      wedding_date: weddingData?.wedding_date || "",
      ceremony_time: weddingData?.ceremony_time || "",
      wedding_location: weddingData?.wedding_location || "",
      hotel_address: weddingData?.hotel_address || "",
      bride_parent_surname: weddingData?.bride_parent_surname || "",
      groom_parent_surname: weddingData?.groom_parent_surname || "",
      guest_count: weddingData?.guest_count || "",
      ceremony_type: weddingData?.ceremony_type || "wedding",
    });

    setAccountForm((prev) => ({
      ...prev,
      email: weddingData?.email || user?.email || "",
    }));
  }, [weddingData, user]);

  const fetchGuests = useCallback(async () => {
    try {
      if (!user) return;

      const groomSnapshot = await getDocs(
        collection(db, "users", user.uid, groomCollectionName)
      );

      const brideSnapshot = await getDocs(
        collection(db, "users", user.uid, brideCollectionName)
      );

      const groomData = groomSnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const brideData = brideSnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setGroomGuests(groomData);
      setBrideGuests(brideData);
    } catch (error) {
      console.error("Error fetching guests:", error);
    }
  }, [user, groomCollectionName, brideCollectionName]);

  useEffect(() => {
    if (!user || weddingLoading) return;
    fetchGuests();
  }, [user, weddingLoading, fetchGuests]);

  const getGuestStats = (groups) => {
    let total = 0;
    let pending = 0;
    let confirmed = 0;
    let notComing = 0;

    groups.forEach((group) => {
      const list = Array.isArray(group.guests) ? group.guests : [];
      total += list.length;

      list.forEach((g) => {
        switch (g.status) {
          case "Pending":
            pending++;
            break;
          case "Participation Confirmed":
            confirmed++;
            break;
          case "Not Participating":
            notComing++;
            break;
          default:
            break;
        }
      });
    });

    return { total, pending, confirmed, notComing };
  };

  const groomStats = getGuestStats(groomGuests);
  const brideStats = getGuestStats(brideGuests);

  const totalGuests = groomStats.total + brideStats.total;
  const totalPending = groomStats.pending + brideStats.pending;
  const totalConfirmed = groomStats.confirmed + brideStats.confirmed;
  const totalNotComing = groomStats.notComing + brideStats.notComing;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "guest_count" ? value.replace(/\D/g, "") : value,
    }));
  };

  const handleCeremonyTypeToggle = async () => {
    if (!user || !isEditing || isExpired) return;

    const currentType = formData.ceremony_type || "wedding";
    const nextType =
      currentType === "home coming" ? "wedding" : "home coming";

    const confirmMessage =
      currentType === "wedding"
        ? "Are you sure you want to change this as home coming?"
        : "Are you sure you want to change this as wedding?";

    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) return;

    try {
      setSaveMessage("");

      const batch = writeBatch(db);

      const userRef = doc(db, "users", user.uid);
      batch.update(userRef, {
        ceremony_type: nextType,
      });

      if (weddingData?.slug) {
        const siteRef = doc(db, "sites", weddingData.slug);
        batch.update(siteRef, {
          ceremony_type: nextType,
        });
      }

      await batch.commit();

      setFormData((prev) => ({
        ...prev,
        ceremony_type: nextType,
      }));

      setSaveMessage(
        `Ceremony type updated successfully to ${
          nextType === "home coming" ? "Home Coming" : "Wedding"
        }.`
      );
    } catch (error) {
      console.error("Error updating ceremony type:", error);
      setSaveMessage("Failed to update ceremony type. Please try again.");
    }
  };

  const handleAccountChange = (e) => {
    const { name, value } = e.target;

    setAccountForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditToggle = () => {
    if (isExpired) return;
    setSaveMessage("");
    setIsEditing((prev) => !prev);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveMessage("");
    setFormData({
      bride_name: weddingData?.bride_name || "",
      groom_name: weddingData?.groom_name || "",
      wedding_date: weddingData?.wedding_date || "",
      ceremony_time: weddingData?.ceremony_time || "",
      wedding_location: weddingData?.wedding_location || "",
      hotel_address: weddingData?.hotel_address || "",
      bride_parent_surname: weddingData?.bride_parent_surname || "",
      groom_parent_surname: weddingData?.groom_parent_surname || "",
      guest_count: weddingData?.guest_count || "",
      ceremony_type: weddingData?.ceremony_type || "wedding",
    });
  };

  const handleSave = async () => {
    if (!user || isExpired) return;

    setIsSaving(true);
    setSaveMessage("");

    try {
      const updatedWeddingData = {
        bride_name: formData.bride_name,
        groom_name: formData.groom_name,
        wedding_date: formData.wedding_date,
        ceremony_time: formData.ceremony_time || "",
        wedding_location: formData.wedding_location,
        hotel_address: formData.hotel_address,
        bride_parent_surname: formData.bride_parent_surname,
        groom_parent_surname: formData.groom_parent_surname,
        guest_count: Number(formData.guest_count || 0),
        ceremony_type: formData.ceremony_type || "wedding",
      };

      const batch = writeBatch(db);

      const userRef = doc(db, "users", user.uid);
      batch.update(userRef, updatedWeddingData);

      if (weddingData?.slug) {
        const siteRef = doc(db, "sites", weddingData.slug);
        batch.update(siteRef, updatedWeddingData);
      }

      await batch.commit();

      setSaveMessage("Wedding details updated successfully.");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating wedding details:", error);
      setSaveMessage("Failed to update details. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateAccount = async () => {
    if (!user || !auth.currentUser) return;

    const trimmedEmail = accountForm.email.trim();
    const currentPassword = accountForm.currentPassword;
    const newPassword = accountForm.newPassword;
    const confirmPassword = accountForm.confirmPassword;

    setAccountMessage("");

    if (!trimmedEmail) {
      setAccountMessage("Email is required.");
      return;
    }

    if (!currentPassword) {
      setAccountMessage("Please enter your current password.");
      return;
    }

    if (newPassword || confirmPassword) {
      if (newPassword.length < 6) {
        setAccountMessage("New password must be at least 6 characters.");
        return;
      }

      if (newPassword !== confirmPassword) {
        setAccountMessage("New password and confirm password do not match.");
        return;
      }
    }

    try {
      setIsUpdatingAccount(true);

      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword
      );

      await reauthenticateWithCredential(auth.currentUser, credential);

      if (trimmedEmail !== auth.currentUser.email) {
        await updateEmail(auth.currentUser, trimmedEmail);
      }

      if (newPassword) {
        await updatePassword(auth.currentUser, newPassword);
      }

      await updateDoc(doc(db, "users", user.uid), {
        email: trimmedEmail,
      });

      if (weddingData?.slug) {
        await updateDoc(doc(db, "sites", weddingData.slug), {
          email: trimmedEmail,
        });
      }

      setAccountForm({
        email: trimmedEmail,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setAccountMessage("Account details updated successfully.");
    } catch (error) {
      console.error("Error updating account:", error);

      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        setAccountMessage("Current password is incorrect.");
      } else if (error.code === "auth/email-already-in-use") {
        setAccountMessage("That email is already in use.");
      } else {
        setAccountMessage("Failed to update account. Please try again.");
      }
    } finally {
      setIsUpdatingAccount(false);
    }
  };

  const detailFields = [
    { label: "Bride Name", name: "bride_name", type: "text" },
    { label: "Groom Name", name: "groom_name", type: "text" },
    { label: "Wedding Date", name: "wedding_date", type: "date" },
    { label: "Ceremony Time", name: "ceremony_time", type: "time" },
    { label: "Wedding Location", name: "wedding_location", type: "text" },
    { label: "Hotel Address", name: "hotel_address", type: "text", full: true },
    { label: "Bride Parent Surname", name: "bride_parent_surname", type: "text" },
    { label: "Groom Parent Surname", name: "groom_parent_surname", type: "text" },
    { label: "Guest Count", name: "guest_count", type: "text" },
  ];

  if (!user || weddingLoading || themeLoading || !siteTheme) {
    return <div className="dash__loading">Loading dashboard...</div>;
  }

  return (
    <section className="dash" aria-label="Dashboard" style={dashboardThemeStyle}>
      <div className="dash__shell">
        <div className="dash__header">
          <div>
            <p className="dash__eyebrow">Dashboard</p>
            <h1 className="dash__title">
              {bride_name} &amp; {groom_name}
            </h1>
          </div>
        </div>

        <div className="dash__message">
          Package: <strong>{isPremium ? "Premium" : "Trial"}</strong> | Expiry:{" "}
          <strong>{expiryText}</strong>
          {isExpired ? " | This account is now read-only for wedding details." : ""}
        </div>

        <div className="dashTabs" role="tablist" aria-label="Dashboard sections">
          <button
            type="button"
            className={`dashTabs__tab ${activeTab === "overview" ? "is-active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            type="button"
            className={`dashTabs__tab ${activeTab === "details" ? "is-active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            Wedding Details
          </button>
          <button
            type="button"
            className={`dashTabs__tab ${activeTab === "account" ? "is-active" : ""}`}
            onClick={() => setActiveTab("account")}
          >
            Account
          </button>
        </div>

        {saveMessage && <p className="dash__message">{saveMessage}</p>}

        {activeTab === "overview" && (
          <div className="dashPanel">
            <div className="dashPanel__head">
              <h2 className="dashPanel__title">Guest Analytics</h2>
              <p className="dashPanel__subtitle">
                A quick view of invitations and RSVP progress
              </p>
            </div>

            <div className="dash__stats">
              <div className="dashStat dashStat--total">
                <div className="dashStat__icon"><FaUsers /></div>
                <p className="dashStat__label">Total Guests</p>
                <h3 className="dashStat__value">{totalGuests}</h3>

                <div className="dashStat__rows">
                  <div className="dashStat__row">
                    <FaClock />
                    <span className="dashStat__rowLabel">Pending</span>
                    <span className="dashStat__chip">{totalPending}</span>
                  </div>
                  <div className="dashStat__row">
                    <FaCheck />
                    <span className="dashStat__rowLabel">Confirmed</span>
                    <span className="dashStat__chip">{totalConfirmed}</span>
                  </div>
                  <div className="dashStat__row">
                    <FaTimes />
                    <span className="dashStat__rowLabel">Not Coming</span>
                    <span className="dashStat__chip">{totalNotComing}</span>
                  </div>
                </div>
              </div>

              <div className="dashStat">
                <div className="dashStat__icon"><FaMale /></div>
                <p className="dashStat__label">{groom_name}&apos;s Side</p>
                <h3 className="dashStat__value">{groomStats.total}</h3>

                <div className="dashStat__rows">
                  <div className="dashStat__row">
                    <FaClock />
                    <span className="dashStat__rowLabel">Pending</span>
                    <span className="dashStat__chip">{groomStats.pending}</span>
                  </div>
                  <div className="dashStat__row">
                    <FaCheck />
                    <span className="dashStat__rowLabel">Confirmed</span>
                    <span className="dashStat__chip">{groomStats.confirmed}</span>
                  </div>
                  <div className="dashStat__row">
                    <FaTimes />
                    <span className="dashStat__rowLabel">Not Coming</span>
                    <span className="dashStat__chip">{groomStats.notComing}</span>
                  </div>
                </div>
              </div>

              <div className="dashStat">
                <div className="dashStat__icon"><FaFemale /></div>
                <p className="dashStat__label">{bride_name}&apos;s Side</p>
                <h3 className="dashStat__value">{brideStats.total}</h3>

                <div className="dashStat__rows">
                  <div className="dashStat__row">
                    <FaClock />
                    <span className="dashStat__rowLabel">Pending</span>
                    <span className="dashStat__chip">{brideStats.pending}</span>
                  </div>
                  <div className="dashStat__row">
                    <FaCheck />
                    <span className="dashStat__rowLabel">Confirmed</span>
                    <span className="dashStat__chip">{brideStats.confirmed}</span>
                  </div>
                  <div className="dashStat__row">
                    <FaTimes />
                    <span className="dashStat__rowLabel">Not Coming</span>
                    <span className="dashStat__chip">{brideStats.notComing}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "details" && (
          <div className="dashPanel">
            <div className="dashPanel__head dashPanel__head--split">
              <div>
                <h2 className="dashPanel__title">Wedding Details</h2>
                <p className="dashPanel__subtitle">
                  {isExpired
                    ? "This section is read-only because the package has expired."
                    : "Update your wedding information anytime"}
                </p>
              </div>

              <div className="dashPanel__actions">
                {!isEditing ? (
                  <ButtonComponent
                    className="dash__actionBtn"
                    onClick={handleEditToggle}
                    disabled={isExpired}
                  >
                    <FaEdit style={{ marginRight: 8 }} />
                    Edit Details
                  </ButtonComponent>
                ) : (
                  <>
                    <ButtonComponent className="dashDetails__cancelBtn" onClick={handleCancel}>
                      Cancel
                    </ButtonComponent>
                    <ButtonComponent
                      className="dash__actionBtn"
                      onClick={handleSave}
                      disabled={isSaving || isExpired}
                    >
                      <FaSave style={{ marginRight: 8 }} />
                      {isSaving ? "Saving..." : "Save Changes"}
                    </ButtonComponent>
                  </>
                )}
              </div>
            </div>

            <div className="dashDetails__grid">
              <div className="dashField dashField--full">
                <label className="dashField__label">Ceremony Type</label>

                <div className="dashToggleWrap">
                  <span
                    className={`dashToggleLabel ${
                      formData.ceremony_type === "wedding" ? "is-active" : ""
                    }`}
                  >
                    Use as Wedding
                  </span>

                  <button
                    type="button"
                    className={`dashToggle ${
                      formData.ceremony_type === "home coming" ? "is-homecoming" : ""
                    }`}
                    onClick={handleCeremonyTypeToggle}
                    disabled={!isEditing || isExpired}
                    aria-label={
                      formData.ceremony_type === "home coming"
                        ? "Use as Home Coming"
                        : "Use as Wedding"
                    }
                  />

                  <span
                    className={`dashToggleLabel ${
                      formData.ceremony_type === "home coming" ? "is-active" : ""
                    }`}
                  >
                    Use as Home Coming
                  </span>
                </div>
              </div>

              {detailFields.map((field) => (
                <div
                  key={field.name}
                  className={`dashField ${field.full ? "dashField--full" : ""}`}
                >
                  <label className="dashField__label" htmlFor={field.name}>
                    {field.label}
                  </label>

                  {field.name === "hotel_address" ? (
                    <textarea
                      id={field.name}
                      name={field.name}
                      className="dashField__input dashField__textarea"
                      value={formData[field.name]}
                      onChange={handleChange}
                      disabled={!isEditing || isExpired}
                    />
                  ) : (
                    <input
                      id={field.name}
                      name={field.name}
                      type={field.type}
                      className="dashField__input"
                      value={formData[field.name]}
                      onChange={handleChange}
                      disabled={
                        field.name === "wedding_date" ? true : !isEditing || isExpired
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "account" && (
          <div className="dashPanel">
            <div className="dashPanel__head">
              <h2 className="dashPanel__title">Account</h2>
              <p className="dashPanel__subtitle">
                Update your email and password here
              </p>
            </div>

            {accountMessage && <p className="dash__message">{accountMessage}</p>}

            <div className="dashDetails__grid">
              <div className="dashField dashField--full">
                <label className="dashField__label" htmlFor="accountEmail">
                  Email
                </label>
                <input
                  id="accountEmail"
                  name="email"
                  type="email"
                  className="dashField__input"
                  value={accountForm.email}
                  onChange={handleAccountChange}
                />
              </div>

              <div className="dashField dashField--full">
                <label className="dashField__label" htmlFor="currentPassword">
                  Current Password
                </label>
                <input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  className="dashField__input"
                  value={accountForm.currentPassword}
                  onChange={handleAccountChange}
                />
              </div>

              <div className="dashField">
                <label className="dashField__label" htmlFor="newPassword">
                  New Password
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  className="dashField__input"
                  value={accountForm.newPassword}
                  onChange={handleAccountChange}
                />
              </div>

              <div className="dashField">
                <label className="dashField__label" htmlFor="confirmPassword">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  className="dashField__input"
                  value={accountForm.confirmPassword}
                  onChange={handleAccountChange}
                />
              </div>
            </div>

            <div className="dashPanel__actions" style={{ marginTop: "1rem" }}>
              <ButtonComponent
                className="dash__actionBtn"
                onClick={handleUpdateAccount}
                disabled={isUpdatingAccount}
              >
                {isUpdatingAccount ? "Updating..." : "Update Account"}
              </ButtonComponent>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Dashboard;